import { useMemo } from 'react';
import {
  FilterableSelect,
  type SelectOption,
} from '../common/FilterableSelect';
import { useAiModels, type AiModelDto } from '../../api/queries/aiModels';

interface ModelPickerProps {
  value: string;
  onChange: (model: string) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

/**
 * Model selector dropdown that fetches available AI models from the API.
 * Default model is pre-selected (Claude Sonnet 4.6).
 * Grouped by provider with display names.
 */
export function ModelPicker({
  value,
  onChange,
  label = 'AI Model',
  size = 'small',
  fullWidth = true,
}: ModelPickerProps) {
  const { data: models, isLoading } = useAiModels();

  const options = useMemo<SelectOption[]>(() => {
    if (!models) return [];
    return models.map((m: AiModelDto) => ({
      value: m.model,
      label: `${m.displayName}${m.isDefault ? ' (Default)' : ''}`,
    }));
  }, [models]);

  const defaultModel = useMemo(
    () => models?.find((m: AiModelDto) => m.isDefault)?.model ?? '',
    [models],
  );

  return (
    <FilterableSelect
      options={options}
      value={value || defaultModel}
      onChange={onChange}
      label={label}
      placeholder={isLoading ? 'Loading models...' : 'Select a model'}
      size={size}
      fullWidth={fullWidth}
      allowEmpty={false}
    />
  );
}

/**
 * Extracts provider name from a model identifier.
 * e.g., "claude-sonnet-4-6" -> "anthropic", "grok-4" -> "grok"
 */
export function getProviderForModel(
  model: string,
  models: AiModelDto[] | undefined,
): string | undefined {
  return models?.find((m) => m.model === model)?.provider;
}
